import { z } from 'zod';

import { COMPENSATION_UNITS, OPPORTUNITY_KINDS } from '../lib/casting';
import { resolveLivestreamEmbed } from '../lib/livestream-embed';

/**
 * An organizer-set apply link renders as a public `<a href>`, and
 * `z.string().url()` accepts `javascript:` / `data:`. This is the FOURTH path
 * into `auditions.external_link` — the organizer's own audition form — which
 * the import pipeline's `httpUrlSchema` never sees (2026-07-26 promote-gate
 * review, High 2). Same rule, enforced where this pipe starts.
 */
const httpUrl = z.url().refine(
  (value) => {
    try {
      const protocol = new URL(value).protocol;
      return protocol === 'http:' || protocol === 'https:';
    } catch {
      return false;
    }
  },
  {
    error: 'validation:invalidUrl',
  },
);

// Status enums
export const AuditionStatusSchema = z.enum([
  'draft',
  'open',
  'closed',
  'archived',
]);
export const AuditionTypeSchema = z.enum(['private', 'public']);
export const ContractLengthSchema = z.enum(['life', 'seasonal', 'shortTerm']);
export const OpportunityKindSchema = z.enum(OPPORTUNITY_KINDS);
export const CompensationUnitSchema = z.enum(COMPENSATION_UNITS);
export const ApplicationStatusSchema = z.enum([
  'open',
  'issues',
  'submitted',
  'accepted',
  'completed',
  'rejected',
  'archived',
]);

// Location schema
export const AuditionLocationSchema = z.object({
  address: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  country: z.string().optional(),
  postalCode: z.string().optional(),
  coordinates: z
    .object({
      lat: z.number(),
      lng: z.number(),
    })
    .optional(),
});

// Create audition schema
export const CreateAuditionSchema = z.object({
  title: z
    .string()
    .transform((v) => v.trim())
    .pipe(z.string().min(1).max(50)),
  accountId: z.guid(),
  // When true, create as a competition (is_competition=true) with the GDO
  // self-serve defaults: hide_apply_button + seeded application structure.
  // Record type is set at creation only — never flippable afterwards.
  isCompetition: z.boolean().default(false),
});

// Optional override of the client's default pay on an event audition. Only the
// keys that are set replace the client default (get_audition_pay_terms).
export const AuditionPayTermsSchema = z
  .object({
    show_day_fee: z.number().min(0).max(100000),
    per_diem: z.number().min(0).max(100000),
    dance_material_fee_male: z.number().min(0).max(100000),
    dance_material_fee_female: z.number().min(0).max(100000),
    // One-time rehearsal / learning fee per dancer. No client default: shown only when set.
    rehearsal_fee: z.number().min(0).max(100000),
    currency: z
      .string()
      .transform((v) => v.toUpperCase())
      .pipe(z.string().regex(/^[A-Z]{3}$/)),
  })
  .partial()
  .strict();

// Date input for MCP tool schemas. A bare z.coerce.date() has no JSON Schema
// form ("Date cannot be represented in JSON Schema") and makes tools/list fail
// for every client, so the wire type is a string and the parsed value a Date.
const toolDateTime = z
  .string()
  .min(1)
  .describe('ISO date or date-time, e.g. 2027-02-20')
  .transform((value) => new Date(value))
  .refine((date) => !Number.isNaN(date.getTime()));

// Create the one audition of a Fever event. The owner account, the pay and the
// event data are read from the event; the caller only supplies what differs.
export const CreateAuditionForEventSchema = z.object({
  eventId: z.guid(),
  title: z
    .string()
    .transform((v) => v.trim())
    .pipe(z.string().min(1).max(50))
    .optional(),
  minAge: z.number().int().min(0).max(99).optional(),
  closingDate: toolDateTime.optional(),
  referenceVideoUrl: httpUrl.optional(),
  payTerms: AuditionPayTermsSchema.optional(),
});

// Dates of an event audition (MCP manageAuditionDates). Plain YYYY-MM-DD strings,
// so no timezone shifts the day (Critical Rule 19).
const calendarDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const clockTime = z.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/);

export const ManageEventAuditionDatesSchema = z
  .object({
    auditionId: z.guid(),
    action: z.enum(['add', 'update', 'remove']),
    dateId: z.guid().optional().describe('For update and remove'),
    startDate: calendarDay.optional().describe('For add'),
    endDate: calendarDay.nullable().optional(),
    startTime: clockTime.nullable().optional(),
    endTime: clockTime.nullable().optional(),
    location: AuditionLocationSchema.nullable().optional(),
  })
  .refine((v) => v.action === 'add' || v.dateId, {
    path: ['dateId'],
  })
  .refine((v) => v.action !== 'add' || v.startDate, {
    path: ['startDate'],
  });

// Status change of applications of one event audition (MCP updateApplications).
// Emails to dancers are off unless notifyCandidate is true; for more than one
// recipient the caller must repeat the recipient count.
export const UpdateEventApplicationsSchema = z
  .object({
    auditionId: z.guid(),
    applicationIds: z.array(z.guid()).min(1).max(100),
    status: z.enum(['accepted', 'rejected', 'issues', 'archived']).optional(),
    candidateNumber: z.int().min(1).max(100000).nullable().optional(),
    notifyCandidate: z.boolean().default(false),
    confirmNotifyCount: z.int().optional(),
  })
  .refine((v) => v.status !== undefined || v.candidateNumber !== undefined, {})
  .refine(
    (v) => v.candidateNumber === undefined || v.applicationIds.length === 1,
    {
      path: ['candidateNumber'],
    },
  )
  .refine(
    (v) =>
      !v.notifyCandidate ||
      v.applicationIds.length === 1 ||
      v.confirmNotifyCount === v.applicationIds.length,
    {
      path: ['confirmNotifyCount'],
    },
  );

// Answer of the database function accept_event_applications.
export const AcceptEventApplicationsResponseSchema = z.object({
  accepted_ids: z.array(z.guid()),
  // Applications that were already accepted: nothing was changed for them.
  unchanged_ids: z.array(z.guid()),
  // The accepted rows as the function read them inside its transaction.
  applications: z.array(z.looseObject({ id: z.guid(), status: z.string() })),
  not_found: z.array(z.guid()),
  previous_status: z.record(z.string(), z.string()),
  pool_added: z.array(z.guid()),
  pool_reactivated: z.array(z.guid()),
  pool_already_active: z.array(z.guid()),
  participants_added: z.array(z.guid()),
  participants_kept: z.array(z.guid()),
});

// One application of an event audition (MCP getAuditionApplication).
export const GetEventApplicationSchema = z.object({
  auditionId: z.guid(),
  applicationId: z.guid(),
});

// Soft delete of an event audition (MCP deleteAudition). The phrase and the
// reason are the confirmation; applications stay in the database.
export const DeleteEventAuditionSchema = z.object({
  auditionId: z.guid(),
  reason: z.string().trim().min(3).max(500),
  confirmationPhrase: z.literal('DELETE'),
});

// Audition date schema
export const AuditionDateSchema = z.object({
  startDate: z.coerce.date<Date>(),
  endDate: z.coerce.date<Date>().optional(),
  startTime: z
    .string()
    .regex(/^([01]\d|2[0-3]):([0-5]\d)$/)
    .optional(),
  endTime: z
    .string()
    .regex(/^([01]\d|2[0-3]):([0-5]\d)$/)
    .optional(),
  ageMin: z.int().min(0).optional(),
  ageMax: z.int().min(0).optional(),
  location: AuditionLocationSchema.optional(),
});

// Update audition schema
const UpdateAuditionFieldsSchema = z.object({
  // Basic Info
  title: z
    .string()
    .min(1)
    .max(50)
    .transform((v) => v.trim())
    .optional(),
  description: z.string().optional(),
  auditionType: AuditionTypeSchema.optional(),
  // BAL-1205: 'job' = a paid casting, listed on /castings.
  opportunityKind: OpportunityKindSchema.optional(),
  externalLink: httpUrl.optional().or(z.literal('')),
  rankIds: z.array(z.guid()).optional(),

  // Requirements
  requirementGenders: z.array(z.string()).optional(),
  requirementAgeMin: z.int().min(0).nullable().optional(),
  requirementAgeMax: z.int().min(0).nullable().optional(),
  requirementHeightMaleMin: z.int().min(0).nullable().optional(),
  requirementHeightMaleMax: z.int().min(0).nullable().optional(),
  requirementHeightFemaleMin: z.int().min(0).nullable().optional(),
  requirementHeightFemaleMax: z.int().min(0).nullable().optional(),
  requirementOther: z.string().optional(),
  requirementMedia: z.array(z.string()).optional(),
  requirementDancePositions: z.array(z.string()).optional(),

  // Contract
  contractLength: ContractLengthSchema.nullable().optional(),
  contractStartDate: z.coerce.date<Date>().nullable().optional(),
  contractEndDate: z.coerce.date<Date>().nullable().optional(),
  // Pay shown publicly on a casting. Null amount = not shown. The currency is
  // required with an amount (DB check auditions_compensation_currency_required).
  compensationAmount: z.number().min(0).max(99_999_999).nullable().optional(),
  // Empty clears the currency (the mapper stores null). Otherwise three
  // letters, the same shape as DB check auditions_compensation_currency_check.
  compensationCurrency: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^([A-Z]{3})?$/, 'validation:rules.currencyMustBeAThreeLetterCode')
    .nullable()
    .optional(),
  compensationUnit: CompensationUnitSchema.nullable().optional(),
  // Blank lines from the one-per-line textarea are dropped, not rejected.
  roles: z
    .array(z.string().trim().max(60))
    .transform((roles) => roles.filter(Boolean))
    .pipe(z.array(z.string()).max(20))
    .optional(),

  // Application Settings
  openingDate: z.coerce.date<Date>().nullable().optional(),
  closingDate: z.coerce.date<Date>().nullable().optional(),
  autoClose: z.boolean().optional(),
  applyOnApp: z.boolean().optional(),
  byInvitationOnly: z.boolean().optional(),
  // Competition-only auto-accept toggle. When true, submitted registrations
  // skip organizer review and land on `accepted`; enabling it also
  // retroactively accepts the competition's existing `completed` rows (handled
  // in updateAuditionAction). Update-only — the DB default `false` covers
  // creation, so it is intentionally absent from CreateAuditionSchema.
  autoAcceptRegistrations: z.boolean().optional(),
  // When true (DB default), entrants may keep editing their registration after
  // acceptance (bounded by the open registration window); re-submitting follows
  // autoAcceptRegistrations. Applies to all auditions. Update-only — the DB
  // default `true` covers creation, so it is absent from CreateAuditionSchema.
  allowAcceptedEdits: z.boolean().optional(),
  // Shared access code that unlocks the Apply button for invitation-only
  // competitions. Edited on the Entry settings tab next to byInvitationOnly;
  // an empty string clears it (the mapper coerces '' → null).
  accessCode: z.string().max(50).nullable().optional(),
  videoPreSelection: z.boolean().optional(),
  applicationFeeAmount: z.number().min(0).nullable().optional(),
  applicationFeeCurrency: z.string().length(3).optional(),

  // Location
  useOrganizationLocation: z.boolean().optional(),
  datesUseSeparateLocations: z.boolean().optional(),

  // Additional
  additionalInformations: z.string().optional(),
  featured: z.boolean().optional(),

  // Competition Settings
  // NOTE: `isCompetition` is intentionally NOT updatable here. Record type is
  // set at creation only (`CreateAuditionSchema.isCompetition`) and is never
  // flippable afterwards (Locked Decision #7 / R7). The post-creation editor
  // exposes no toggle, and the update path must not carry it either — otherwise
  // a crafted request could flip a live audition into a competition (or back),
  // stranding seeded competition structure / partner portal state.
  //
  // `allowsMultiCategoryEntry` is also gone: under the split styles/category
  // model (PR #1171) one application holds every style the dancer competes in,
  // so a per-application multi-entry toggle is obsolete.
  hideApplyButton: z.boolean().optional(),
  isNetworkingEvent: z.boolean().optional(),
  hasFinalShow: z.boolean().optional(),
  // BAL-408: when false, hide the "My Meetings" tab in the competition
  // partner portal (and 404 the page). Defaults true on the row.
  showPartnerMeetings: z.boolean().optional(),
  hideIndividualParticipants: z.boolean().optional(),
  hideOrganizationParticipants: z.boolean().optional(),
  // US-128: admin gate revealing the participant list/profiles to PARTNER
  // organizations. Default false (hidden) on the row; enforced in RLS.
  participantsVisibleToPartners: z.boolean().optional(),
  networkingAllowOrgSelection: z.boolean().optional(),
  networkingAllowIndividualSelection: z.boolean().optional(),
  networkingOrgSelectionStart: z.coerce.date<Date>().nullable().optional(),
  networkingOrgSelectionEnd: z.coerce.date<Date>().nullable().optional(),
  networkingIndividualSelectionStart: z.coerce
    .date<Date>()
    .nullable()
    .optional(),
  networkingIndividualSelectionEnd: z.coerce.date<Date>().nullable().optional(),
  networkingShowMatchesToOrgs: z.boolean().optional(),
  networkingShowMatchesToIndividuals: z.boolean().optional(),
  competitionBranding: z
    .object({
      // Cover image is picked from the organization's media library; we store
      // the organization_media id and resolve it to a public URL at render.
      // (name/logo dropped: the public title is audition.title and the logo is
      // always the organization's logo — see the branding rework.)
      cover_media_id: z.guid().optional().or(z.literal('')),
      accent_color: z
        .string()
        .regex(/^#[0-9a-fA-F]{6}$/)
        .optional()
        .or(z.literal('')),
      // http(s) only, like `externalLink` above. This renders as a public
      // `<a href>` on the audition page (public-audition-view-apply-rail.tsx,
      // public-audition-view.tsx) and is organizer-writable, so a bare
      // `z.string().url()` accepts `javascript:`/`data:` and stores an
      // executable href — the exact class this file's `httpUrl` was added to
      // close, missed 95 lines below it (2026-07-26 final gate, H-A). Its
      // siblings here were already hardened: `livestream_embed_url` has a host
      // allowlist and `support_button_destination` is mailto/https-only.
      website_url: httpUrl.optional().or(z.literal('')),
      // US-134 — livestream embed URL. Validated against the host allowlist
      // server-side (resolveLivestreamEmbed); a URL whose host is not an
      // approved provider, or that is not https, is rejected here so a
      // clickjacking / embed-abuse URL can never reach the iframe src.
      livestream_embed_url: z
        .string()
        .trim()
        .optional()
        .or(z.literal(''))
        .superRefine((val, ctx) => {
          if (!val) return; // empty clears the field
          if (resolveLivestreamEmbed(val) === null) {
            ctx.addIssue({
              code: 'custom',
              message:
                'Enter an https URL from a supported provider (YouTube, Vimeo, Twitch, Dailymotion, or Facebook).',
            });
          }
        }),
      // US-135 — per-competition support button. Label falls back to the
      // default when empty; destination accepts a mailto:, wa.me/, or https
      // URL. An https destination opens in a new tab.
      support_button_label: z.string().max(40).optional().or(z.literal('')),
      support_button_destination: z
        .string()
        .trim()
        .optional()
        .or(z.literal(''))
        .superRefine((val, ctx) => {
          if (!val) return;
          const isMailto = /^mailto:[^\s@]+@[^\s@]+\.[^\s@]+$/i.test(val);
          const isHttps = /^https:\/\/\S+$/i.test(val);
          if (!isMailto && !isHttps) {
            ctx.addIssue({
              code: 'custom',
              message:
                'Enter a mailto: address, a WhatsApp https://wa.me/ link, or an https URL.',
            });
          }
        }),
    })
    .nullable()
    .optional(),
});

export const UpdateAuditionSchema = UpdateAuditionFieldsSchema.superRefine(
  (val, ctx) => {
    // An amount needs a currency (DB check
    // auditions_compensation_currency_required_check). Reported on the currency
    // field so the editor shows it there instead of a generic save failure.
    const hasAmount =
      val.compensationAmount !== null && val.compensationAmount !== undefined;
    if (
      hasAmount &&
      val.compensationCurrency !== undefined &&
      !val.compensationCurrency
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['compensationCurrency'],
        message: 'validation:rules.payAmountNeedsACurrency',
      });
    }
  },
);

// Change an event audition (MCP updateAudition). Only the passed fields change;
// null clears the nullable ones. The settings shared with the admin form reuse
// UpdateAuditionFieldsSchema (the schema before its currency rule), so a limit changes in one place. Competition, contract,
// fee and external-link settings are deliberately absent: they do not apply to
// a Fever event audition or would bypass its required form fields.
export const UpdateAuditionForEventSchema = z.object({
  ...UpdateAuditionFieldsSchema.pick({
    title: true,
    description: true,
    additionalInformations: true,
    requirementGenders: true,
    requirementAgeMin: true,
    requirementAgeMax: true,
    requirementHeightMaleMin: true,
    requirementHeightMaleMax: true,
    requirementHeightFemaleMin: true,
    requirementHeightFemaleMax: true,
    requirementDancePositions: true,
    requirementOther: true,
    autoClose: true,
    featured: true,
  }).shape,
  openingDate: toolDateTime.nullable().optional(),
  closingDate: toolDateTime.nullable().optional(),
  auditionId: z.guid().describe('The audition to change'),
  status: z
    .enum(['draft', 'open', 'closed', 'archived'])
    .optional()
    .describe('open publishes; closed stops applications'),
  closeReason: z
    .enum(['filled', 'manual'])
    .optional()
    .describe('filled (default) = show cast'),
  referenceVideoUrl: httpUrl.nullable().optional(),
  payTerms: AuditionPayTermsSchema.nullable()
    .optional()
    .describe(
      'Override of the client default pay, key by key. null goes back to the client defaults',
    ),
  rebuildApplicationForm: z
    .boolean()
    .optional()
    .describe(
      'Rebuild the form from the event and minimum age; replaces manual edits',
    ),
});

// Publish audition schema
export const PublishAuditionSchema = z.object({
  auditionId: z.guid(),
});

// The library items the dancer picked or uploaded in an apply form this session.
// The server attaches each one that is the dancer's own and is in the saved
// answers (`sync_own_application_media`); ids already in `fields` grant nothing
// on their own, since an organizer may write that column.
export const AttachMediaIdsSchema = z.array(z.guid()).max(200);

// Application schema
//
// Styles/categories separation (2026-06-04): the dancer multi-selects
// style entries (competition_categories rows). Required when the
// competition defines style entries — enforced in the action, because
// plain auditions without entries legitimately submit none. The dancer's
// age division is server-computed, never client-supplied.
export const CreateApplicationSchema = z.object({
  auditionId: z.guid(),
  styleEntryIds: z.array(z.guid()).max(50).optional(),
  fields: z.record(z.string(), z.unknown()).optional(),
  // Access code carried from the public unlock step. For an invitation-only
  // competition where the applicant has no invitation yet, the create action
  // redeems this code server-side (creating the invitation) so that unlocking
  // never required a dancer profile up front — the profile requirement lives
  // here, at registration, where it belongs.
  accessCode: z.string().min(1).max(50).optional(),
  attachMediaIds: AttachMediaIdsSchema.optional(),
});

export const UpdateApplicationSchema = z.object({
  status: ApplicationStatusSchema.optional(),
  fields: z.record(z.string(), z.unknown()).optional(),
  managementFields: z.record(z.string(), z.unknown()).optional(),
  attachMediaIds: AttachMediaIdsSchema.optional(),
});

// Filter schema
export const AuditionFilterSchema = z.object({
  status: z.array(AuditionStatusSchema).optional(),
  auditionType: z.array(AuditionTypeSchema).optional(),
  genders: z.array(z.string()).optional(),
  search: z.string().optional(),
  accountId: z.guid().optional(),
  featured: z.boolean().optional(),
  // Record-type filter for the organizer list (true = competitions,
  // false = auditions, undefined = both).
  isCompetition: z.boolean().optional(),
  page: z.int().min(1).default(1),
  pageSize: z.int().min(1).max(100).default(20),
});

// ==================== Application Form Builder Schemas ====================

// Field type enum
export const ApplicationFieldTypeSchema = z.enum([
  'text',
  'textarea',
  'number',
  'email',
  'phone',
  'date',
  'select',
  'multiselect',
  'checkbox',
  'checkboxGroup',
  'radio',
  'file',
  'video',
  'videoCategories',
  'experience',
  'competition_styles',
  'age_category',
  'institution_preferences',
  'url',
  'heading',
  'paragraph',
]);

// Field option schema
export const FieldOptionSchema = z.object({
  value: z.string().min(1),
  label: z.string().min(1),
});

// Field validation schema
export const FieldValidationSchema = z.object({
  required: z.boolean().optional(),
  minLength: z.int().min(0).optional(),
  maxLength: z.int().min(1).optional(),
  min: z.number().optional(),
  max: z.number().optional(),
  pattern: z.string().optional(),
  patternMessage: z.string().optional(),
  fileTypes: z.array(z.string()).optional(),
  maxFileSize: z.int().min(0).optional(),
});

// Field condition schema
export const FieldConditionSchema = z.object({
  fieldKey: z.string().min(1),
  operator: z.enum([
    'equals',
    'notEquals',
    'contains',
    'notContains',
    'isEmpty',
    'isNotEmpty',
    'greaterThan',
    'lessThan',
    'greaterOrEqual',
    'lessOrEqual',
    'onOrBefore',
    'onOrAfter',
  ]),
  value: z
    .union([z.string(), z.number(), z.boolean(), z.array(z.string())])
    .optional(),
});

// Single field configuration schema
export const ApplicationFieldConfigSchema = z.object({
  key: z
    .string()
    .min(1)
    .max(50)
    .regex(
      /^[a-z][a-zA-Z0-9_]*$/,
      'validation:rules.keyMustStartWithLowercaseLetter',
    ),
  type: ApplicationFieldTypeSchema,
  label: z.string().min(1).max(100),
  placeholder: z.string().max(200).optional(),
  description: z.string().max(500).optional(),
  defaultValue: z.unknown().optional(),
  options: z.array(FieldOptionSchema).optional(),
  validation: FieldValidationSchema.optional(),
  conditions: z.array(FieldConditionSchema).optional(),
  conditionLogic: z.enum(['and', 'or']).optional(),
  width: z.enum(['full', 'half', 'third']).optional(),
  sortOrder: z.int().min(0),
  isManagementField: z.boolean().optional(),
  source: z.literal('profile').optional(),
  sourceKey: z.string().max(100).optional(),
  allowMultiple: z.boolean().optional(),
  maxFiles: z.int().min(1).max(50).optional(),
  locked: z.boolean().optional(),
  widget: z.literal('country').optional(),
});

// Step (group) schema — mirrors the Meteor application-process group shape.
export const ApplicationStepSchema = z.object({
  key: z
    .string()
    .min(1)
    .max(50)
    .regex(
      /^[a-z][a-zA-Z0-9_]*$/,
      'validation:rules.stepKeyMustStartWithLowercaseLetter',
    ),
  name: z.string().min(1).max(100),
  icon: z.string().max(50).optional(),
  fields: z.array(ApplicationFieldConfigSchema),
  displayConditions: z.array(FieldConditionSchema).optional(),
  conditionLogic: z.enum(['and', 'or']).optional(),
  sortOrder: z.int().min(0),
});

// Application structure schema. `groups` (step model) is optional and
// preferred when present; `fields` (legacy flat) is retained for back-compat.
export const ApplicationStructureSchema = z.object({
  version: z.int().min(1).default(1),
  fields: z.array(ApplicationFieldConfigSchema),
  groups: z.array(ApplicationStepSchema).optional(),
  settings: z
    .object({
      showProgressIndicator: z.boolean().optional(),
      allowSaveDraft: z.boolean().optional(),
      requireAllFields: z.boolean().optional(),
    })
    .optional(),
});

// Template ID schema
export const ApplicationTemplateIdSchema = z.enum([
  'basic',
  'dance_company',
  'workshop',
  'networking_event',
  'video_audition',
  'fever_casting',
  'blank',
]);

// Update audition application structure schema
export const UpdateApplicationStructureSchema = z.object({
  auditionId: z.guid(),
  applicationStructure: ApplicationStructureSchema,
});

// Field edits of an event audition form (MCP editAuditionForm). Operations run
// in order; the protected event fields cannot be removed or made optional.
const FormFieldAddSchema = ApplicationFieldConfigSchema.omit({
  sortOrder: true,
});

export const EditEventAuditionFormSchema = z.object({
  auditionId: z.guid(),
  operations: z
    .array(
      z.discriminatedUnion('op', [
        z.object({
          op: z.literal('add'),
          field: FormFieldAddSchema,
          afterKey: z.string().optional(),
        }),
        z.object({
          op: z.literal('update'),
          key: z.string(),
          changes: ApplicationFieldConfigSchema.omit({
            key: true,
            sortOrder: true,
          }).partial(),
        }),
        z.object({ op: z.literal('remove'), key: z.string() }),
        z.object({
          op: z.literal('move'),
          key: z.string(),
          afterKey: z.string(),
        }),
      ]),
    )
    .min(1)
    .max(30),
});

// One tool for every non-destructive edit of an event audition (MCP
// manageEventAudition). `section` picks which of the three edit schemas applies.
// MCP tool parameters must be one flat object, so the three shapes are merged
// with every field optional; the tool then validates the fields of the chosen
// section against that section's own schema, which keeps one source of limits.
const optionalFields = (schema: { shape: Record<string, z.ZodType> }) => {
  const { auditionId: _auditionId, ...rest } = schema.shape;
  return z.object(rest).partial().shape;
};

export const ManageEventAuditionSchema = z.object({
  section: z.enum(['settings', 'dates', 'form']),
  auditionId: z.guid(),
  ...optionalFields(UpdateAuditionForEventSchema),
  ...optionalFields(ManageEventAuditionDatesSchema),
  ...optionalFields(EditEventAuditionFormSchema),
  // Dates stay strings on the wire; the settings section turns them into Dates
  // when it validates, so they are not transformed twice.
  openingDate: z
    .string()
    .nullable()
    .optional()
    .describe('ISO date or date-time'),
  closingDate: z
    .string()
    .nullable()
    .optional()
    .describe('ISO date or date-time'),
  // Wire form of payTerms, location and the form edits: the full schemas cost
  // more tokens than the catalogue can spare. The tool checks each against its
  // section schema before anything runs.
  payTerms: z
    .record(z.string(), z.unknown())
    .nullable()
    .optional()
    .describe(
      'section settings: pay override {show_day_fee, per_diem, dance_material_fee_male, dance_material_fee_female, currency}; null restores client defaults',
    ),
  location: z
    .record(z.string(), z.unknown())
    .nullable()
    .optional()
    .describe('section dates: {address, city, state, country, postalCode}'),
  operations: z
    .array(z.record(z.string(), z.unknown()))
    .max(30)
    .optional()
    .describe(
      'section form: ordered edits. add {op, field: {key, type, label, ...}, afterKey?}; update {op, key, changes}; remove {op, key}; move {op, key, afterKey}',
    ),
});

// Apply template schema
export const ApplyTemplateSchema = z.object({
  auditionId: z.guid(),
  templateId: ApplicationTemplateIdSchema,
});

// ==================== Networking/Selection Schemas ====================

// Selection item schema
export const NetworkingSelectionItemSchema = z.object({
  organizationId: z.guid(),
  rank: z.int().min(1).max(10),
  notes: z.string().max(500).optional(),
});

// Submit selections schema
export const SubmitNetworkingSelectionsSchema = z.object({
  auditionId: z.guid(),
  selections: z
    .array(NetworkingSelectionItemSchema)
    .min(1)
    .max(10)
    .refine(
      (items) => {
        const ranks = items.map((i) => i.rank);
        return new Set(ranks).size === ranks.length;
      },
      {
        error: 'validation:rules.eachRankMustBeUnique',
      },
    ),
});

// Export types
export type CreateAuditionInput = z.infer<typeof CreateAuditionSchema>;
export type CreateAuditionForEventInput = z.infer<
  typeof CreateAuditionForEventSchema
>;
export type ManageEventAuditionDatesInput = z.infer<
  typeof ManageEventAuditionDatesSchema
>;
export type EditEventAuditionFormInput = z.infer<
  typeof EditEventAuditionFormSchema
>;
export type UpdateEventApplicationsInput = z.infer<
  typeof UpdateEventApplicationsSchema
>;
export type DeleteEventAuditionInput = z.infer<
  typeof DeleteEventAuditionSchema
>;
export type ManageEventAuditionInput = z.infer<
  typeof ManageEventAuditionSchema
>;
export type UpdateAuditionForEventInput = z.infer<
  typeof UpdateAuditionForEventSchema
>;
export type AuditionPayTermsInput = z.infer<typeof AuditionPayTermsSchema>;
export type UpdateAuditionInput = z.infer<typeof UpdateAuditionSchema>;
export type AuditionDateInput = z.infer<typeof AuditionDateSchema>;
export type CreateApplicationInput = z.infer<typeof CreateApplicationSchema>;
export type UpdateApplicationInput = z.infer<typeof UpdateApplicationSchema>;
export type AuditionFilterInput = z.infer<typeof AuditionFilterSchema>;
export type ApplicationFieldConfigInput = z.infer<
  typeof ApplicationFieldConfigSchema
>;
export type ApplicationStructureInput = z.infer<
  typeof ApplicationStructureSchema
>;
export type UpdateApplicationStructureInput = z.infer<
  typeof UpdateApplicationStructureSchema
>;
export type ApplyTemplateInput = z.infer<typeof ApplyTemplateSchema>;
export type SubmitNetworkingSelectionsInput = z.infer<
  typeof SubmitNetworkingSelectionsSchema
>;
